import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useInputBindingPress } from '@sorrem-client/ui/hooks';
import { getStoredSessionToken } from '@sorrem-client/ui/shared/auth-session';
import { BugReportModal } from './BugReportModal';

/** Opens the bug report; none outside a `BugReportHost`. */
const OpenBugReportContext = createContext<(() => void) | null>(null);

/**
 * The bug report (#218) for the whole app: the `report-bug` binding and the
 * form, mounted once at the root so a tester can report from every page,
 * the gathering and a run's loading screen included. Buttons open it with
 * `useOpenBugReport`. It needs a session, so it never opens without one.
 */
export function BugReportHost({ children }: { readonly children: ReactNode }): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const open = useCallback((): void => {
        if (getStoredSessionToken() !== null) {
            setIsOpen(true);
        }
    }, []);
    useInputBindingPress('report-bug', open);

    return (
        <OpenBugReportContext.Provider value={open}>
            {children}
            {isOpen && <BugReportModal onClose={() => setIsOpen(false)} />}
        </OpenBugReportContext.Provider>
    );
}

/** Opens the bug report form; a no-op outside a `BugReportHost`. */
export function useOpenBugReport(): () => void {
    return useContext(OpenBugReportContext) ?? noop;
}

function noop(): void {
    // Outside a BugReportHost there is no form to open.
}
